/**
 * Pen style for the draw tool, persisted in `sessionStorage` (ahooks) so the
 * next stroke — and the next canvas — inherits the last tip, colour and weight.
 * Same shape as `useCanvasShapeAndEdgeStyle`, one tool narrower.
 */

import { useSessionStorageState } from "ahooks";
import { DEFAULT_EDGE_COLOR } from "../nodes/shared/CanvasColorPalettes";
import {
  normalizeCanvasStrokeWidth,
  type CanvasStrokeWidth,
} from "../types/canvasNodeData";
import { normalizeDrawingTip, type DrawingTip } from "../utils/drawingTips";

export type DrawStyleState = {
  tip: DrawingTip;
  color: string;
  strokeWidth: CanvasStrokeWidth;
  /** The eraser is a fourth tip that takes ink away; the rest stays put. */
  erasing: boolean;
};

export const DEFAULT_DRAW_STYLE: DrawStyleState = {
  tip: "pen",
  color: DEFAULT_EDGE_COLOR,
  strokeWidth: 2,
  erasing: false,
};

const DRAW_STYLE_KEY = "canvas:drawStyle";

function deserializeDrawStyle(raw: string): DrawStyleState {
  try {
    const parsed = JSON.parse(raw) as Partial<DrawStyleState>;
    return {
      tip: normalizeDrawingTip(parsed.tip),
      color: parsed.color ?? DEFAULT_DRAW_STYLE.color,
      strokeWidth: normalizeCanvasStrokeWidth(parsed.strokeWidth),
      erasing: parsed.erasing === true,
    };
  } catch {
    return DEFAULT_DRAW_STYLE;
  }
}

export function useCanvasDrawStyle() {
  const [drawStyle, setDrawStyle] = useSessionStorageState<DrawStyleState>(
    DRAW_STYLE_KEY,
    {
      defaultValue: DEFAULT_DRAW_STYLE,
      deserializer: deserializeDrawStyle,
      listenStorageChange: true,
    },
  );

  const style = drawStyle ?? DEFAULT_DRAW_STYLE;

  return {
    drawTip: style.tip,
    drawColor: style.color,
    drawStrokeWidth: style.strokeWidth,
    drawErasing: style.erasing,
    setDrawStyle: (updates: {
      tip?: DrawingTip;
      color?: string;
      strokeWidth?: number;
      erasing?: boolean;
    }) =>
      setDrawStyle((prev) => {
        const base = prev ?? DEFAULT_DRAW_STYLE;
        return {
          tip: updates.tip ?? base.tip,
          color: updates.color ?? base.color,
          strokeWidth:
            updates.strokeWidth === undefined
              ? base.strokeWidth
              : normalizeCanvasStrokeWidth(updates.strokeWidth),
          // Picking a tip puts the eraser away; nothing else does.
          erasing: updates.erasing ?? (updates.tip ? false : base.erasing),
        };
      }),
  };
}
